import { ChevronDownIcon, Clock01Icon } from "@/icons";
import { cn } from "@/lib/cn";
import {
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  TimePicker,
} from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import {
  useCreateOfficeMutation,
  useGetOfficeByIdQuery,
  useUpdateOfficeMutation,
} from "@/app/features/control-panel/organization/organization.api";
import CustomDrawer from "@/components/CustomDrawer";
import type { CustomModalProps } from "@/components/CustomModal";
import GeoFenceMap from "@/components/GeoFanceMap";
import WithLoader from "@/components/WithLoader";
import { WEEK_DAYS_OPTIONS } from "@/constants/global.constants";
import useAuth from "@/hooks/useAuth";
import { useTimeZone } from "@/hooks/useTimeZone";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";

type Props = CustomModalProps & { id?: string; type?: string };

export default function ManageOffices({ open, handleClose, id, type }: Props) {
  const isUpdateMode = !!id;
  const [form] = Form.useForm();
  const { options: timezoneOptions } = useTimeZone();
  const { profileInfo } = useAuth();
  const organization = profileInfo?.organization;
  // Watched values
  const radius = Form.useWatch("radius", form);
  const geoFenceEnabled = Form.useWatch("geo_fence", form);
  const startTime = Form.useWatch("default_shift_start_time", form);
  const lat = Form.useWatch("latitude", form);
  const long = Form.useWatch("longitude", form);

  // API hooks
  const [createOffice, { isLoading: isCreating }] = useCreateOfficeMutation();
  const [updateOffice, { isLoading: isUpdating }] = useUpdateOfficeMutation();

  // Get single office
  const {
    data: officeData,
    isLoading: isLoadingOffice,
    isFetching: isFetchingOffice,
  } = useGetOfficeByIdQuery(id || "", {
    skip: !id || !open,
  });

  // Handle form submission
  const handleSubmit = async (values: any) => {
    const {
      working_days,
      default_shift_end_time,
      default_shift_start_time,
      ...rest
    } = values || {};
    try {
      const payload = {
        ...rest,
        location: organization?.name || "Default Location",
        working_days: working_days || [],
        default_shift_end_time: default_shift_end_time?.format("HH:mm"),
        default_shift_start_time: default_shift_start_time?.format("HH:mm"),
      };

      let res;
      if (isUpdateMode && id) {
        res = await updateOffice({ id, ...payload }).unwrap();
        // Check if response indicates business logic failure
        if (res && res.success === false) {
          const errorMessage = getErrorMessage(res, "Failed to update office");
          toast("error", errorMessage);
          return;
        }
        toast("success", "Office updated successfully");
      } else {
        res = await createOffice({ ...payload, status: "ACTIVE" }).unwrap();
        // Check if response indicates business logic failure
        if (res && res.success === false) {
          const errorMessage = getErrorMessage(res, "Failed to create office");
          toast("error", errorMessage);
          return;
        }
        toast("success", "Office created successfully");
      }

      handleClose();
      form.resetFields();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, "Failed to save office");
      toast("error", errorMessage);
      // console.error('Failed to save office:', error);
    }
  };

  // Populate form when in update mode
  useEffect(() => {
    if (isUpdateMode && officeData?.data && open) {
      const { default_shift_end_time, default_shift_start_time, ...office } =
        officeData.data;

      form.setFieldsValue({
        ...office,
        default_shift_start_time: dayjs(default_shift_start_time, "HH:mm"),
        default_shift_end_time: dayjs(default_shift_end_time, "HH:mm"),
      });
    } else if (!isUpdateMode && open) {
      // Reset form for create mode
      form.resetFields();
    }
  }, [officeData, isUpdateMode, open, form]);

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={
        type === "VIEW"
          ? "View Office"
          : isUpdateMode
            ? "Update Office"
            : "Add New Office"
      }
    >
      <WithLoader
        isLoading={isLoadingOffice || isFetchingOffice}
        className={cn(
          "rounded-lg",
          (isLoadingOffice || isFetchingOffice) && "h-[calc(100vh_-_105px)]",
        )}
      >
        <Form
          disabled={type === "VIEW"}
          form={form}
          size="large"
          layout="vertical"
          className="custom-form space-y-6"
          onFinish={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <Form.Item
              label="Office Name"
              name="name"
              rules={[{ required: true, message: "Please enter office name" }]}
            >
              <Input placeholder="Office Name" />
            </Form.Item>
            <Form.Item
              label="Office Type"
              name="type"
              rules={[{ required: true, message: "Please select office type" }]}
            >
              <Select
                placeholder="Select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={[
                  { value: "HEAD", label: "Head Office" },
                  { value: "BRANCH", label: "Branch Office" },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Address"
              name="address"
              rules={[{ required: true, message: "Please enter address" }]}
            >
              <Input placeholder="Write here..." />
            </Form.Item>
            <Form.Item
              label="Time Zone"
              name="timezone"
              rules={[{ required: true, message: "Please select time zone" }]}
            >
              <Select
                showSearch
                placeholder="Select"
                optionFilterProp="label"
                options={timezoneOptions}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
              />
            </Form.Item>

            <div className="col-span-2">
              <Form.Item
                label="Working Days"
                name="working_days"
                rules={[
                  { required: true, message: "Please select at least one day" },
                ]}
              >
                <Checkbox.Group
                  options={WEEK_DAYS_OPTIONS}
                  className="custom-check"
                />
              </Form.Item>
            </div>

            <div className="col-span-2">
              <Form.Item
                label="Add Geo Fence"
                name="geo_fence"
                layout="horizontal"
                className="border-b border-t !mb-0 !pb-0 border-b-theme-gray-800 border-t-theme-gray-800 mt-3 horizontal-end hide-colon"
                valuePropName="checked"
              >
                <Switch className="custom-switch" />
              </Form.Item>
            </div>

            {geoFenceEnabled && (
              <>
                <div className="col-span-2">
                  <GeoFenceMap
                    viewOnly={type === "VIEW"}
                    // defaultValues={
                    //   officeData?.data?.latitude && officeData?.data?.longitude
                    //     ? {
                    //         lat: parseFloat(officeData?.data?.latitude),
                    //         long: parseFloat(officeData?.data.longitude),
                    //       }
                    //     : undefined
                    // }
                    defaultValues={
                      lat && long
                        ? {
                            lat: lat?.toString(),
                            long: long?.toString(),
                          }
                        : undefined
                    }
                    onMapChange={(latitude, longitude) => {
                      form.setFieldsValue({
                        latitude: latitude.toString(),
                        longitude: longitude.toString(),
                      });
                    }}
                    radius={radius}
                  />
                </div>

                <Form.Item
                  label="Latitude"
                  name="latitude"
                  rules={[{ required: true, message: "Please enter latitude" }]}
                >
                  <Input placeholder="" />
                </Form.Item>

                <Form.Item
                  label="Longitude"
                  name="longitude"
                  rules={[
                    { required: true, message: "Please enter longitude" },
                  ]}
                >
                  <Input placeholder="" />
                </Form.Item>

                <Form.Item
                  label="Set Radius"
                  name="radius"
                  rules={[{ required: true, message: "Please enter radius" }]}
                >
                  <InputNumber
                    controls={false}
                    placeholder="00"
                    suffix="Meters"
                    className="w-full"
                  />
                </Form.Item>

                <Form.Item
                  label="Outside Fence Action"
                  name="outside_fence_action"
                  rules={[
                    {
                      required: true,
                      message: "Please select outside fence action",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    suffixIcon={
                      <ChevronDownIcon size={24} className="text-white" />
                    }
                    options={[
                      { value: "ALLOW_ATTENDANCE", label: "Allow Attendance" },
                      { value: "BLOCK_ATTENDANCE", label: "Block Attendance" },
                    ]}
                  />
                </Form.Item>
              </>
            )}

            <div className="col-span-2">
              <div className="justify-start text-white text-2xl font-normal mt-5">
                Office Time
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Form.Item
                  label="Start Time"
                  name="default_shift_start_time"
                  rules={[
                    { required: true, message: "Please select start time" },
                  ]}
                >
                  <TimePicker
                    format="HH:mm"
                    showNow={false}
                    needConfirm={false}
                    placeholder="HH:MM"
                    suffixIcon={<Clock01Icon size={18} />}
                    onChange={() => {
                      form.resetFields(["default_shift_end_time"]);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="End Time"
                  name="default_shift_end_time"
                  rules={[
                    { required: true, message: "Please select end time" },
                  ]}
                >
                  <TimePicker
                    format="HH:mm"
                    showNow={false}
                    needConfirm={false}
                    placeholder="HH:MM"
                    suffixIcon={<Clock01Icon size={18} />}
                    disabled={!startTime || type === "VIEW"}
                    disabledHours={() => {
                      if (!startTime) return [];
                      const startHour = dayjs(startTime).hour();
                      return Array.from({ length: startHour }, (_, i) => i);
                    }}
                    disabledMinutes={(selectedHour) => {
                      if (!startTime) return [];
                      const startHour = dayjs(startTime).hour();
                      const startMinute = dayjs(startTime).minute();

                      if (selectedHour > startHour) return [];
                      if (selectedHour < startHour)
                        return Array.from({ length: 60 }, (_, i) => i);

                      // Same hour, disable minutes before start minute
                      return Array.from({ length: startMinute }, (_, i) => i);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label="Check-in Grace Period (Minutes)"
                  name="default_shift_check_in_grace_period"
                  rules={[
                    { required: true, message: "Please enter grace period" },
                  ]}
                >
                  <InputNumber
                    controls={false}
                    placeholder="00"
                    suffix="Minutes"
                    min={0}
                    max={120}
                    className="w-full"
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {type !== "VIEW" && (
            <Flex justify="end">
              <Button
                className="btn-primary min-w-[80px] h-11 font-semibold"
                htmlType="submit"
                loading={isCreating || isUpdating}
                disabled={isLoadingOffice}
              >
                {isUpdateMode ? "Update" : "Save"}
              </Button>
            </Flex>
          )}
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
}
